// Core
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  Button,
  Box,
  Grid,
  Flex,
  Text,
  FormControl,
  Input,
  useColorModeValue,
  useDisclosure,
  useBreakpointValue
} from '@chakra-ui/react';

// Accets
import { apiService } from 'services/ApiService';
import { ReactComponent as EditIcon } from 'assets/svg/edit.svg';
import { ReactComponent as CancelIcon } from 'assets/svg/cancel.svg';

// Components
import Card from 'components/Card/Card';
import ErrorCard from 'components/Form/Errors';
import EditField from 'components/Form/EditField';

const ChangePassword = ({ userData }) => {
	const bg = useColorModeValue('white', '#0A1027');
    const boxShadow = useColorModeValue('unset', '0px 1px 3px 0px rgba(0, 0, 0, 0.10),0px 1px 2px 0px rgba(0, 0, 0, 0.06)');

	const [object, setObject] = useState({ currentPassword: null, newPassword: null });
	const [objectErrors, setObjectErrors] = useState([]);
	const [errorCard, setErrorCard] = useState();

	const setData = useCallback((maxValue, target, key) => {
		if (target.length < maxValue) {
		  setObjectErrors([...objectErrors, key]);
		} else {
		  setObjectErrors(objectErrors.filter(error => error !== key));
		}
		setObject({...object, [key]: target}); 
	}, [object, objectErrors, setObject, setObjectErrors]);

	const handleField = useMemo(() => {
		const fieldConfigs = [
		  { field: 'currentPassword', maxValue: 8 },
		  { field: 'newPassword', maxValue: 8 }
		];

		setErrorCard();

		return fieldConfigs.reduce((acc, { field, maxValue }) => {
		  acc[field] = (e) => setData(maxValue, e.target.value, field);
		  return acc;
		}, {});
	}, [setData]);

	const handleChange = async () => {
		setErrorCard();

		if (objectErrors.length > 0) {
      setErrorCard('The passwords are incorrect, try changing the fields');
      return;
    }

    if(object.currentPassword === object.newPassword){
      setErrorCard('You can`t change to the same password');
      return;	
    }

		try {
			const response = await apiService.updateUserPassword(object);
			if(response.statusCode === 403){
				setErrorCard(response?.message);
				return;
			} else if(response.statusCode === 400){
				setErrorCard(response.message);
				return;
			} else if(response?.data === false || 
				response?.data?.status === false || 
				response.statusCode !== 200 || 
				response.statusCode === 404){
				setErrorCard(response?.data?.errorMessage);
				return;
			} 

			setEditMode(false);	
			setObject({ currentPassword: null, newPassword: null });
		} catch(e){
			setErrorCard('An unknown error occurred, try changing the fields or refreshing the page');
			return;
		}
	};

	const [editMode, setEditMode] = useState(false);

	const isMobile = useBreakpointValue({ base: true, md: false });
 
	return (
		<Box mt="8px">
			{ errorCard && ( <ErrorCard error={errorCard} /> )}

			<Card padding={{base: '16px 12px 16px 12px', md: '16px 16px 44px 16px'}}  bg={bg} boxShadow={boxShadow} overflow="hidden">
				<Flex alignItems="center" p="0 0 0 18px" mb="24px" justifyContent="space-between">
					<Text variant="title-lg" fontSize="18px" fontStyle="normal" fontWeight="800" letterSpacing="-1px" textTransform="uppercase">
						Password
					</Text>

					<>
						{ editMode ? (
							<Flex gap="12px">
								<Button onClick={() => {setEditMode(!editMode); setErrorCard();}} variant="delete-md" w={{base: '44px', md: '88px'}}>{isMobile ? (<CancelIcon />) : 'Cancel'}</Button>
								<Button onClick={() => handleChange()}  variant="button-md" w={{base: '72px', md: '124px'}}>Save</Button>
							</Flex>
						) : (
							<Button onClick={() => setEditMode(!editMode)} variant="solid-md" w="88px">Edit <EditIcon fontSize="24px" style={{marginLeft: '6px'}} /></Button>
						)}
					</>
				</Flex>

				<Grid alignItems="center" templateColumns={{ sm: '1fr', lg: '1fr 1fr 1fr' }} gap="10px" templateRows='1fr' zIndex="2">
			        <EditField editMode={editMode} id="currentPassword" label={ editMode ? 'Current password' : 'Password'} type="password" value={ editMode ? object.currentPassword ? object.currentPassword : '' : 'xxxxxxxxx'} isValid={true} onChange={handleField.currentPassword} />

			        { editMode && (
			        	<EditField editMode={editMode} id="newPassword" label="New password" type="password" value={object.newPassword} isValid={true} onChange={handleField.newPassword} />
			        )}
				</Grid>
			</Card>

		</Box>
	);
};


export default ChangePassword;